import { z } from 'zod';
import { EventTypeSchema } from './calendar';
import { ProductGroupSchema } from './customer';

/**
 * Config key namespace. Engine M4 resolves these at compute time. All values
 * are versioned + immutable — every "edit" creates a new row.
 */
export const ConfigKindSchema = z.enum([
  'MONTHLY_MULTIPLIER', // 12 entries (T1..T12)
  'EVENT_MULTIPLIER', // 6 entries (NORMAL..MEGA_SALE)
  'UPH_NORM', // units-per-hour per (warehouse × customer × product_group × pk_type)
  'CUTOFF_DAYS', // overrides per event_type (rarely used)
]);
export type ConfigKind = z.infer<typeof ConfigKindSchema>;

export const PkTypeSchema = z.enum(['STANDARD', 'BULKY', 'FRAGILE', 'COMBO']);
export type PkType = z.infer<typeof PkTypeSchema>;

/**
 * Composite key, JSON-serialised, used as the "config_key" string. Examples:
 *   monthly:6
 *   event:MEGA_SALE
 *   uph:wh_vn_hn_01:LOREAL-VN:COSMETIC:STANDARD
 *   cutoff:MEGA_SALE
 */
export const ConfigKeyShape = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('MONTHLY_MULTIPLIER'), month: z.number().int().min(1).max(12) }),
  z.object({ kind: z.literal('EVENT_MULTIPLIER'), event_type: EventTypeSchema }),
  z.object({
    kind: z.literal('UPH_NORM'),
    warehouse_id: z.string(),
    customer_id: z.string(),
    product_group: ProductGroupSchema,
    pk_type: PkTypeSchema,
  }),
  z.object({ kind: z.literal('CUTOFF_DAYS'), event_type: EventTypeSchema }),
]);
export type ConfigKey = z.infer<typeof ConfigKeyShape>;

export function configKeyToString(k: ConfigKey): string {
  switch (k.kind) {
    case 'MONTHLY_MULTIPLIER':
      return `monthly:${k.month}`;
    case 'EVENT_MULTIPLIER':
      return `event:${k.event_type}`;
    case 'UPH_NORM':
      return `uph:${k.warehouse_id}:${k.customer_id}:${k.product_group}:${k.pk_type}`;
    case 'CUTOFF_DAYS':
      return `cutoff:${k.event_type}`;
  }
}

export const ConfigValueSchema = z.object({
  id: z.string(),
  key: z.string(), // configKeyToString(k)
  kind: ConfigKindSchema,
  value: z.number(), // numeric — multipliers/UPH/days. JSON for future complex configs.
  version: z.number().int().positive(),
  is_current: z.boolean(),
  effective_from: z.string(), // ISO date
  note: z.string().default(''),
  created_at: z.string(),
  created_by: z.string(),
});
export type ConfigValue = z.infer<typeof ConfigValueSchema>;

export interface ConfigKeyView {
  key: string;
  kind: ConfigKind;
  label: string; // human readable, VN
  current: ConfigValue | null;
  history: ConfigValue[]; // newest first
}
